import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Download, FileSpreadsheet, Filter, X, RefreshCw, Layers,
  Building2, MapPin, FileText, Zap, BarChart3, Loader2, AlertCircle, FileDown,
} from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { siteService } from '../services/siteService';
import { organisationService } from '../services/organisationService';
import { retailContractService, dmaContractService } from '../services/contractService';
import type { BvSite, BvOrganisation, BvContact, BvDma, BvRetailContract } from '../types/billValidationTypes';
import {
  flattenClient, flattenSite, flattenDma,
  flattenRetailContract, flattenRates,
  exportToXLSX, exportToCSV, exportMultiSheetXLSX,
} from '../utils/exportUtils';
import { ComplianceReportDocument } from '../components/reports/ComplianceReportDocument';
import { downloadReportAsPdf } from '../utils/pdfUtils';

// ── Types ──────────────────────────────────────────────────────────────────────
// 'contacts' is not its own export entity — contact data is folded into each
// Site row instead (see flattenSite's contact columns). Contacts still has
// its own management page; this only affects the Export tab.
type ExportEntity = 'clients' | 'sites' | 'dma' | 'bv_contracts' | 'bv_contract_rates';

type Slice<T> = { data: T[]; loading: boolean; error: string | null };

function mkSlice<T>(): Slice<T> {
  return { data: [], loading: true, error: null };
}

// Assembled view used by export functions
type AllData = {
  organisations:   BvOrganisation[];
  contacts:        (BvContact & { client_name?: string; client_sf_account_id?: string })[];
  sites:           BvSite[];
  dma:             (BvDma & { _site?: unknown })[];
  retailContracts: (BvRetailContract & { _site?: unknown })[];
};

const ENTITY_TABS: { value: ExportEntity; label: string }[] = [
  { value: 'clients',           label: 'Clients' },
  { value: 'sites',             label: 'Sites' },
  { value: 'dma',               label: 'DMA Contracts' },
  { value: 'bv_contracts',      label: 'BV Contracts' },
  { value: 'bv_contract_rates', label: 'BV Contract Rates' },
];

const QUICK_EXPORT_CONFIG = [
  { key: 'clients'           as ExportEntity, label: 'Clients',       icon: Building2, sliceKey: 'orgs'      as const },
  { key: 'sites'             as ExportEntity, label: 'Sites',         icon: MapPin,    sliceKey: 'sites'     as const },
  { key: 'dma'               as ExportEntity, label: 'DMA Contracts', icon: FileText,  sliceKey: 'dma'       as const },
  { key: 'bv_contracts'      as ExportEntity, label: 'BV Contracts',  icon: Zap,       sliceKey: 'contracts' as const },
  { key: 'bv_contract_rates' as ExportEntity, label: 'BV Rates',      icon: BarChart3, sliceKey: 'contracts' as const },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function getEntityRows(key: ExportEntity, data: AllData): Record<string, unknown>[] {
  switch (key) {
    case 'clients':           return data.organisations.map(flattenClient);
    case 'sites':             return data.sites.map(flattenSite);
    case 'dma':               return data.dma.map(flattenDma);
    case 'bv_contracts':      return data.retailContracts.map(flattenRetailContract);
    case 'bv_contract_rates': return data.retailContracts.flatMap(flattenRates);
  }
}

function getEntityCount(key: ExportEntity, data: AllData): number {
  switch (key) {
    case 'clients':           return data.organisations.length;
    case 'sites':             return data.sites.length;
    case 'dma':               return data.dma.length;
    case 'bv_contracts':      return data.retailContracts.length;
    case 'bv_contract_rates': return data.retailContracts.reduce((n, c) => n + (c.rates?.length ?? 0), 0);
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function FilterSelect({
  label, value, onChange, options,
}: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  if (options.length === 0) return null;
  return (
    <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
      <SelectTrigger className={cn('h-9 w-40 text-xs shrink-0', value && 'border-blue-400 text-blue-700')}>
        <SelectValue placeholder={`All ${label}s`} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All {label}s</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SectionHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
    </div>
  );
}

function StatBadge({ val, label, colorClass }: { val: string | number; label: string; colorClass: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium', colorClass)}>
      {val} {label}
    </span>
  );
}

function SliceStatus({ loading, error }: { loading: boolean; error: string | null }) {
  if (loading) return <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />;
  // The tooltip has to sit on a wrapping element: a lucide icon forwards its
  // props to the <svg>, and `title` is not a prop it accepts, so the message was
  // typed as an error and gave the reader no tooltip either way.
  if (error)   return <span title={error}><AlertCircle className="h-3.5 w-3.5 text-red-400" /></span>;
  return null;
}

// ── Page ───────────────────────────────────────────────────────────────────────
export function ExportPage() {
  // ── Per-entity slices ─────────────────────────────────────────────────────
  const [orgs,      setOrgs]      = useState<Slice<BvOrganisation>>(mkSlice());
  const [contacts,  setContacts]  = useState<Slice<BvContact & { client_name?: string; client_sf_account_id?: string }>>(mkSlice());
  const [sites,     setSites]     = useState<Slice<BvSite>>(mkSlice());
  const [dma,       setDma]       = useState<Slice<BvDma & { _site?: unknown }>>(mkSlice());
  const [contracts, setContracts] = useState<Slice<BvRetailContract & { _site?: unknown }>>(mkSlice());

  const [fullExporting, setFullExporting] = useState(false);

  // Filtered export state
  const [entity,       setEntity]       = useState<ExportEntity>('clients');
  const [search,       setSearch]       = useState('');
  const [clientF,      setClientF]      = useState('');
  const [stateF,       setStateF]       = useState('');
  const [cityF,        setCityF]        = useState('');
  const [distF,        setDistF]        = useState('');
  const [retailerF,    setRetailerF]    = useState('');
  const [tariffF,      setTariffF]      = useState('');
  const [bvStatusF,    setBvStatusF]    = useState('');

  // Smart export state
  const [smartClient, setSmartClient] = useState('');
  const [smartNmiRaw, setSmartNmiRaw] = useState('');

  // PDF report state
  const [pdfClient,      setPdfClient]      = useState('');
  const [pdfGenerating,  setPdfGenerating]  = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  // ── Individual loaders ────────────────────────────────────────────────────
  const loadOrgs = useCallback(async () => {
    setOrgs((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await organisationService.list();
      setOrgs({ data: res.data ?? [], loading: false, error: res.success ? null : (res.error ?? 'Failed') });
    } catch (e) {
      setOrgs({ data: [], loading: false, error: (e as Error).message });
    }
  }, []);

  const loadContacts = useCallback(async () => {
    setContacts((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await organisationService.listAllContacts();
      setContacts({
        data: (res.data ?? []) as (BvContact & { client_name?: string; client_sf_account_id?: string })[],
        loading: false,
        error: res.success ? null : (res.error ?? 'Failed'),
      });
    } catch (e) {
      setContacts({ data: [], loading: false, error: (e as Error).message });
    }
  }, []);

  const loadSites = useCallback(async () => {
    setSites((s) => ({ ...s, loading: true, error: null }));
    try {
      // Use the RPC-backed listAll which handles RLS correctly
      const res = await siteService.listAll();
      setSites({ data: res.data ?? [], loading: false, error: res.success ? null : (res.error ?? 'Failed') });
    } catch (e) {
      setSites({ data: [], loading: false, error: (e as Error).message });
    }
  }, []);

  const loadDma = useCallback(async () => {
    setDma((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await dmaContractService.listAll();
      setDma({
        data: (res.data ?? []) as (BvDma & { _site?: unknown })[],
        loading: false,
        error: res.success ? null : (res.error ?? 'Failed'),
      });
    } catch (e) {
      setDma({ data: [], loading: false, error: (e as Error).message });
    }
  }, []);

  const loadContracts = useCallback(async () => {
    setContracts((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await retailContractService.listAll();
      setContracts({
        data: (res.data ?? []) as (BvRetailContract & { _site?: unknown })[],
        loading: false,
        error: res.success ? null : (res.error ?? 'Failed'),
      });
    } catch (e) {
      setContracts({ data: [], loading: false, error: (e as Error).message });
    }
  }, []);

  const loadAll = useCallback(() => {
    loadOrgs();
    loadContacts();
    loadSites();
    loadDma();
    loadContracts();
  }, [loadOrgs, loadContacts, loadSites, loadDma, loadContracts]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Assembled AllData (used by export functions + derived memo) ───────────
  // Contacts don't export as their own sheet — folded onto each Site row
  // instead (flattenSite reads site._contacts), joined by contact.site_id.
  const data: AllData | null = useMemo(() => {
    if (orgs.loading || contacts.loading || sites.loading || dma.loading || contracts.loading) return null;
    const contactsBySiteId = new Map<string, typeof contacts.data>();
    for (const c of contacts.data) {
      if (!c.site_id) continue;
      const list = contactsBySiteId.get(c.site_id);
      if (list) list.push(c); else contactsBySiteId.set(c.site_id, [c]);
    }
    const sitesWithContacts = sites.data.map((s) => ({ ...s, _contacts: contactsBySiteId.get(s.id) ?? [] }));
    return {
      organisations:   orgs.data,
      contacts:        contacts.data,
      sites:           sitesWithContacts,
      dma:             dma.data,
      retailContracts: contracts.data,
    };
  }, [orgs, contacts, sites, dma, contracts]);

  const allDone = !orgs.loading && !contacts.loading && !sites.loading && !dma.loading && !contracts.loading;

  // ── Slice count helpers (available even while other slices load) ──────────
  const sliceCounts: Record<typeof QUICK_EXPORT_CONFIG[number]['sliceKey'], { loading: boolean; count: number; error: string | null }> = useMemo(() => ({
    orgs:      { loading: orgs.loading,      count: orgs.data.length,      error: orgs.error },
    sites:     { loading: sites.loading,     count: sites.data.length,     error: sites.error },
    dma:       { loading: dma.loading,       count: dma.data.length,       error: dma.error },
    contracts: { loading: contracts.loading, count: contracts.data.length, error: contracts.error },
  }), [orgs, sites, dma, contracts]);

  // ── Option lists ──────────────────────────────────────────────────────────
  const clientOptions   = useMemo(() => [...new Set(sites.data.map((s) => s.client?.name ?? '').filter(Boolean))].sort(), [sites.data]);
  const stateOptions    = useMemo(() => [...new Set(sites.data.map((s) => s.bv_extn?.state ?? s.addr_state ?? '').filter(Boolean))].sort(), [sites.data]);
  const cityOptions     = useMemo(() => [...new Set(sites.data.map((s) => s.addr_city ?? '').filter(Boolean))].sort(), [sites.data]);
  const distOptions     = useMemo(() => [...new Set(sites.data.map((s) => s.bv_extn?.network_distributor ?? '').filter(Boolean))].sort(), [sites.data]);
  const retailerOptions = useMemo(() => [...new Set(contracts.data.map((c) => c.retailer?.name ?? '').filter(Boolean))].sort(), [contracts.data]);
  const tariffOptions   = useMemo(() => [...new Set(sites.data.map((s) => s.bv_extn?.network_tariff_code ?? '').filter(Boolean))].sort(), [sites.data]);
  const bvStatusOptions = ['Enrolled', 'Pending Enrolment', 'Not Enrolled', 'Pending Cancellation', 'Cancelled'];

  // ── Smart NMI match ───────────────────────────────────────────────────────
  const smartNmiSite = useMemo(
    () => sites.data.find((s) => s.nmi === smartNmiRaw.trim().toUpperCase()) ?? null,
    [sites.data, smartNmiRaw],
  );

  // ── Smart client stats ────────────────────────────────────────────────────
  const smartClientStats = useMemo(() => {
    if (!smartClient) return null;
    const clientSites = sites.data.filter((s) => s.client?.name === smartClient);
    const siteIds     = new Set(clientSites.map((s) => s.id));
    return {
      sites:     clientSites.length,
      contacts:  contacts.data.filter((c) => c.client_name === smartClient).length,
      dma:       dma.data.filter((d) => siteIds.has(d.site_id)).length,
      contracts: contracts.data.filter((c) => siteIds.has(c.site_id ?? '')).length,
    };
  }, [sites.data, contacts.data, dma.data, contracts.data, smartClient]);

  // ── Smart NMI stats ───────────────────────────────────────────────────────
  const smartNmiStats = useMemo(() => {
    if (!smartNmiSite) return null;
    const id = smartNmiSite.id;
    return {
      client:      smartNmiSite.client?.name ?? '—',
      distributor: smartNmiSite.bv_extn?.network_distributor as string ?? '—',
      dma:         dma.data.filter((d) => d.site_id === id).length,
      contracts:   contracts.data.filter((c) => c.site_id === id).length,
    };
  }, [smartNmiSite, dma.data, contracts.data]);

  // ── Filtered site IDs ─────────────────────────────────────────────────────
  const filteredSiteIds = useMemo(() => {
    return new Set(
      sites.data.filter((s) => {
        const q = search.toLowerCase();
        if (q && !(s.nmi ?? '').toLowerCase().includes(q) && !(s.client?.name ?? '').toLowerCase().includes(q)) return false;
        if (clientF   && s.client?.name !== clientF) return false;
        if (stateF    && (s.bv_extn?.state ?? s.addr_state ?? '') !== stateF) return false;
        if (cityF     && (s.addr_city ?? '') !== cityF) return false;
        if (distF     && (s.bv_extn?.network_distributor ?? '') !== distF) return false;
        if (retailerF && (s.active_retailer ?? '') !== retailerF) return false;
        if (tariffF   && (s.bv_extn?.network_tariff_code ?? '') !== tariffF) return false;
        if (bvStatusF && (s.bv_extn?.bill_val_status ?? '') !== bvStatusF) return false;
        return true;
      }).map((s) => s.id),
    );
  }, [sites.data, search, clientF, stateF, cityF, distF, retailerF, tariffF, bvStatusF]);

  // ── Filtered export rows ──────────────────────────────────────────────────
  const exportRows = useMemo((): Record<string, unknown>[] => {
    const q = search.toLowerCase();
    switch (entity) {
      case 'clients':
        return orgs.data
          .filter((o) => !q || o.name.toLowerCase().includes(q) || (o.abn ?? '').includes(q))
          .map(flattenClient);
      case 'sites':
        return (data?.sites ?? []).filter((s) => filteredSiteIds.has(s.id)).map(flattenSite);
      case 'dma':
        return dma.data.filter((d) => filteredSiteIds.has(d.site_id)).map(flattenDma);
      case 'bv_contracts':
        return contracts.data
          .filter((c) => filteredSiteIds.has(c.site_id ?? '') && (!retailerF || (c.retailer?.name ?? '') === retailerF))
          .map(flattenRetailContract);
      case 'bv_contract_rates':
        return contracts.data
          .filter((c) => filteredSiteIds.has(c.site_id ?? '') && (!retailerF || (c.retailer?.name ?? '') === retailerF))
          .flatMap(flattenRates);
      default: return [];
    }
  }, [orgs.data, sites.data, data, dma.data, contracts.data, entity, filteredSiteIds, search, clientF, retailerF]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFullExport = useCallback(async () => {
    if (!data) return;
    setFullExporting(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      exportMultiSheetXLSX([
        { name: 'Clients',           rows: data.organisations.map(flattenClient) },
        { name: 'Sites',             rows: data.sites.map(flattenSite) },
        { name: 'DMA Contracts',     rows: data.dma.map(flattenDma) },
        { name: 'BV Contracts',      rows: data.retailContracts.map(flattenRetailContract) },
        { name: 'BV Contract Rates', rows: data.retailContracts.flatMap(flattenRates) },
      ], `bv-full-export-${date}`);
    } finally {
      setFullExporting(false);
    }
  }, [data]);

  const handleExportByClient = useCallback((format: 'csv' | 'xlsx') => {
    if (!data || !smartClient) return;
    const date      = new Date().toISOString().slice(0, 10);
    const safe      = smartClient.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 40);
    const clientSites = data.sites.filter((s) => s.client?.name === smartClient);
    const siteIds   = new Set(clientSites.map((s) => s.id));
    const filtDma       = data.dma.filter((d) => siteIds.has(d.site_id));
    const filtContracts = data.retailContracts.filter((c) => siteIds.has(c.site_id ?? ''));
    const org           = data.organisations.filter((o) => o.name === smartClient);

    if (format === 'xlsx') {
      exportMultiSheetXLSX([
        { name: 'Client',            rows: org.map(flattenClient) },
        { name: 'Sites',             rows: clientSites.map(flattenSite) },
        { name: 'DMA Contracts',     rows: filtDma.map(flattenDma) },
        { name: 'BV Contracts',      rows: filtContracts.map(flattenRetailContract) },
        { name: 'BV Contract Rates', rows: filtContracts.flatMap(flattenRates) },
      ], `bv-client-${safe}-${date}`);
    } else {
      exportToCSV(clientSites.map(flattenSite), `bv-client-${safe}-sites-${date}`);
    }
  }, [data, smartClient]);

  const handleExportByNmi = useCallback((format: 'csv' | 'xlsx') => {
    if (!data || !smartNmiSite) return;
    const date      = new Date().toISOString().slice(0, 10);
    const nmi       = smartNmiSite.nmi ?? smartNmiRaw;
    const safe      = nmi.replace(/[^a-zA-Z0-9_-]/g, '-');
    const id        = smartNmiSite.id;
    const siteDma   = data.dma.filter((d) => d.site_id === id);
    const siteContracts = data.retailContracts.filter((c) => c.site_id === id);
    const siteWithContacts = data.sites.find((s) => s.id === id) ?? smartNmiSite;

    if (format === 'xlsx') {
      exportMultiSheetXLSX([
        { name: 'Site',              rows: [flattenSite(siteWithContacts)] },
        { name: 'DMA Contracts',     rows: siteDma.map(flattenDma) },
        { name: 'BV Contracts',      rows: siteContracts.map(flattenRetailContract) },
        { name: 'BV Contract Rates', rows: siteContracts.flatMap(flattenRates) },
      ], `bv-nmi-${safe}-${date}`);
    } else {
      exportToCSV(siteContracts.flatMap(flattenRates), `bv-nmi-${safe}-rates-${date}`);
    }
  }, [data, smartNmiSite, smartNmiRaw]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const previewRows  = exportRows.slice(0, 10);
  const previewCols  = exportRows.length > 0 ? Object.keys(exportRows[0]) : [];
  const filename     = `bv-${entity}-${new Date().toISOString().slice(0, 10)}`;
  const activeFilters = [search, clientF, stateF, cityF, distF, retailerF, tariffF, bvStatusF].filter(Boolean).length;

  const clearFilters = () => {
    setSearch(''); setClientF(''); setStateF(''); setCityF('');
    setDistF(''); setRetailerF(''); setTariffF(''); setBvStatusF('');
  };

  const showSiteFilters    = entity !== 'clients';
  const showRetailerFilter = entity === 'bv_contracts' || entity === 'bv_contract_rates' || entity === 'sites';

  const today = new Date().toISOString().slice(0, 10);

  const pdfClientObj = useMemo(
    () => (data?.organisations ?? []).find(o => o.name === pdfClient) ?? null,
    [data, pdfClient],
  );

  const pdfContractsByClient = useMemo(() => {
    if (!pdfClient || !data) return [];
    return data.retailContracts.filter(c => {
      const site = c._site as { name?: string } | undefined;
      return site?.name === pdfClient;
    });
  }, [data, pdfClient]);

  const handleGeneratePdf = async () => {
    if (!pdfRef.current || !pdfClientObj) return;
    setPdfGenerating(true);
    try {
      const safeName = pdfClientObj.name.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      await downloadReportAsPdf(pdfRef.current, `compliance-report-${safeName}-${today}.pdf`);
    } finally {
      setPdfGenerating(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const jsx = (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-8">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            Export
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">All export options in one place</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start">
          <Button
            variant="ghost"
            size="sm"
            onClick={loadAll}
            disabled={!allDone}
            className="gap-1.5 text-gray-500"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', !allDone && 'animate-spin')} /> Refresh
          </Button>
          <Button
            onClick={handleFullExport}
            disabled={!data || fullExporting}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Layers className="h-4 w-4" />
            {fullExporting ? 'Building…' : 'Full Export (5 sheets)'}
          </Button>
        </div>
      </div>

      {/* ── Section 0: PDF Client Report ──────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Client PDF Report"
          subtitle="Generate a professional compliance report for a single client — includes sites, contacts, contracts, and rate schedules"
        />
        <Card className="border border-gray-200 shadow-none">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 mb-1.5">Select Client</p>
                <Select
                  value={pdfClient || '__none__'}
                  onValueChange={(v) => setPdfClient(v === '__none__' ? '' : v)}
                >
                  <SelectTrigger className="h-9 text-xs" disabled={sites.loading}>
                    <SelectValue placeholder={sites.loading ? 'Loading…' : 'Select a client…'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select a client…</SelectItem>
                    {clientOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {pdfClientObj && (
                <div className="flex flex-wrap gap-1.5 self-end pb-0.5">
                  <StatBadge val={data?.sites.filter(s => s.name === pdfClient).length ?? 0}     label="Sites"     colorClass="bg-blue-50 text-blue-700" />
                  <StatBadge val={pdfContractsByClient.length}                                    label="Contracts" colorClass="bg-blue-50 text-blue-700" />
                  <StatBadge val={pdfContractsByClient.reduce((n, c) => n + (c.rates?.length ?? 0), 0)} label="Rates" colorClass="bg-blue-50 text-blue-700" />
                </div>
              )}

              <Button
                className="gap-2 bg-red-600 hover:bg-red-700 text-white shrink-0"
                disabled={!pdfClientObj || !data || pdfGenerating}
                onClick={handleGeneratePdf}
              >
                {pdfGenerating
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
                  : <><FileDown className="h-4 w-4" /> Download PDF Report</>
                }
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ── Section 1: Quick Export ─────────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Quick Export"
          subtitle="One-click download of all data — no filters, all rows"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {QUICK_EXPORT_CONFIG.map(({ key, label, icon: Icon, sliceKey }) => {
            const slice  = sliceCounts[sliceKey];
            const count  = key === 'bv_contract_rates'
              ? contracts.data.reduce((n, c) => n + (c.rates?.length ?? 0), 0)
              : slice.count;
            const fname  = `bv-${key}-${today}`;
            const ready  = !slice.loading && !slice.error;
            const rowData: Record<string, unknown>[] | null = ready && data ? getEntityRows(key, data) : null;

            return (
              <Card key={key} className="border border-gray-200 shadow-none hover:shadow-sm transition-shadow">
                <CardContent className="p-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="p-1.5 rounded-md bg-blue-50">
                      <Icon className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                      {slice.loading
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                        : slice.error
                          ? <span title={slice.error}><AlertCircle className="h-3.5 w-3.5 text-red-400" /></span>
                          : count.toLocaleString()
                      }
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-gray-800 leading-tight">{label}</p>
                  <div className="flex gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-7 text-xs gap-1 px-2"
                      disabled={!ready || !rowData || rowData.length === 0}
                      onClick={() => rowData && exportToCSV(rowData, fname)}
                    >
                      <Download className="h-3 w-3" /> CSV
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 h-7 text-xs gap-1 px-2"
                      disabled={!ready || !rowData || rowData.length === 0}
                      onClick={() => rowData && exportToXLSX(rowData, fname)}
                    >
                      <FileSpreadsheet className="h-3 w-3" /> XLS
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ── Section 2: Smart Export ─────────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Smart Export"
          subtitle="Export everything linked to a single client or NMI as a multi-sheet workbook"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* By Client */}
          <Card className="border border-gray-200 shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                Export by Client
                {(orgs.loading || sites.loading || contacts.loading || dma.loading || contracts.loading) && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 ml-auto" />
                )}
              </CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                5 sheets: Client · Sites (with contacts) · DMA · Contracts · Rates
              </p>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <Select value={smartClient || '__none__'} onValueChange={(v) => setSmartClient(v === '__none__' ? '' : v)}>
                <SelectTrigger className="h-9 text-xs" disabled={sites.loading}>
                  <SelectValue placeholder={sites.loading ? 'Loading clients…' : 'Select a client…'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select a client…</SelectItem>
                  {clientOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>

              {smartClientStats && (
                <div className="flex flex-wrap gap-1.5">
                  <StatBadge val={smartClientStats.sites}     label="Sites"     colorClass="bg-blue-50 text-blue-700" />
                  <StatBadge val={smartClientStats.contacts}  label="Contacts"  colorClass="bg-blue-50 text-blue-700" />
                  <StatBadge val={smartClientStats.dma}       label="DMA"       colorClass="bg-blue-50 text-blue-700" />
                  <StatBadge val={smartClientStats.contracts} label="Contracts" colorClass="bg-blue-50 text-blue-700" />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 gap-1.5 text-xs h-9"
                  disabled={!smartClient || !data}
                  onClick={() => handleExportByClient('csv')}
                >
                  <Download className="h-3.5 w-3.5" /> Sites CSV
                </Button>
                <Button
                  className="flex-1 gap-1.5 text-xs h-9"
                  disabled={!smartClient || !data}
                  onClick={() => handleExportByClient('xlsx')}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" /> Multi-sheet XLSX
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* By NMI */}
          <Card className="border border-gray-200 shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <MapPin className="h-4 w-4 text-indigo-600" />
                Export by NMI
                {(sites.loading || dma.loading || contracts.loading) && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 ml-auto" />
                )}
              </CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                4 sheets: Site · DMA Contracts · BV Contracts · Rates
              </p>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="relative">
                <Input
                  placeholder="Type NMI (e.g. 3000000001)…"
                  value={smartNmiRaw}
                  onChange={(e) => setSmartNmiRaw(e.target.value)}
                  className={cn(
                    'h-9 text-xs pr-20',
                    smartNmiRaw && (smartNmiSite ? 'border-green-400' : 'border-red-300'),
                  )}
                />
                {smartNmiRaw && (
                  <span className={cn(
                    'absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium',
                    smartNmiSite ? 'text-green-600' : 'text-red-400',
                  )}>
                    {sites.loading ? '…' : smartNmiSite ? 'matched' : 'not found'}
                  </span>
                )}
              </div>

              {smartNmiStats && (
                <div className="space-y-1.5">
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="px-2 py-1 bg-gray-100 rounded-md text-gray-600">
                      <span className="text-gray-400">Client: </span>{smartNmiStats.client}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded-md text-gray-600">
                      <span className="text-gray-400">Dist: </span>{smartNmiStats.distributor}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <StatBadge val={smartNmiStats.dma}       label="DMA"       colorClass="bg-indigo-50 text-indigo-700" />
                    <StatBadge val={smartNmiStats.contracts} label="Contracts" colorClass="bg-indigo-50 text-indigo-700" />
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 gap-1.5 text-xs h-9"
                  disabled={!smartNmiSite || !data}
                  onClick={() => handleExportByNmi('csv')}
                >
                  <Download className="h-3.5 w-3.5" /> Rates CSV
                </Button>
                <Button
                  className="flex-1 gap-1.5 text-xs h-9"
                  disabled={!smartNmiSite || !data}
                  onClick={() => handleExportByNmi('xlsx')}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" /> Multi-sheet XLSX
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

      {/* ── Section 3: Filtered Export ──────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Filtered Export"
          subtitle="Apply filters, preview the result, then download"
        />

        {/* Entity tabs */}
        <div className="flex flex-wrap gap-1.5">
          {ENTITY_TABS.map(({ value, label }) => {
            const cfg   = QUICK_EXPORT_CONFIG.find((c) => c.key === value);
            const slice = cfg ? sliceCounts[cfg.sliceKey] : null;
            const count = value === entity ? exportRows.length : (slice?.count ?? null);
            return (
              <button
                key={value}
                onClick={() => setEntity(value)}
                className={cn(
                  'px-3.5 py-1.5 rounded-md text-xs font-medium border transition-colors flex items-center gap-1.5',
                  entity === value
                    ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
                )}
              >
                {label}
                {slice?.loading
                  ? <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                  : count != null && (
                    <span className={cn('text-[10px]', entity === value ? 'text-blue-500' : 'text-gray-400')}>
                      {count.toLocaleString()}
                    </span>
                  )
                }
              </button>
            );
          })}
        </div>

        {/* Filter card */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters
                {activeFilters > 0 && (
                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                    {activeFilters}
                  </span>
                )}
              </CardTitle>
              {activeFilters > 0 && (
                <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="h-3.5 w-3.5" /> Clear all
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Input
                placeholder={
                  entity === 'clients' ? 'Search name or ABN…' : 'Search NMI or client…'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 text-xs w-52"
              />
              {showSiteFilters && (
                <>
                  <FilterSelect label="Client"      value={clientF}   onChange={setClientF}   options={clientOptions} />
                  <FilterSelect label="State"       value={stateF}    onChange={setStateF}    options={stateOptions} />
                  <FilterSelect label="City"        value={cityF}     onChange={setCityF}     options={cityOptions} />
                  <FilterSelect label="Distributor" value={distF}     onChange={setDistF}     options={distOptions} />
                  <FilterSelect label="Tariff"      value={tariffF}   onChange={setTariffF}   options={tariffOptions} />
                  <FilterSelect label="BV Status"   value={bvStatusF} onChange={setBvStatusF} options={bvStatusOptions} />
                </>
              )}
              {showRetailerFilter && (
                <FilterSelect label="Retailer" value={retailerF} onChange={setRetailerF} options={retailerOptions} />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Row count + export buttons */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {(() => {
              const cfg   = QUICK_EXPORT_CONFIG.find((c) => c.key === entity);
              const slice = cfg ? sliceCounts[cfg.sliceKey] : null;
              if (slice?.loading) return <span className="flex items-center gap-1.5 text-gray-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</span>;
              return (
                <>
                  <span className="font-semibold text-gray-900">{exportRows.length.toLocaleString()}</span>{' '}
                  row{exportRows.length !== 1 ? 's' : ''} ready
                  {activeFilters > 0 && <span className="text-gray-400 ml-1">(filtered)</span>}
                </>
              );
            })()}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportToCSV(exportRows, filename)}
              disabled={exportRows.length === 0}
              className="gap-1.5 text-xs h-8"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
            <Button
              size="sm"
              onClick={() => exportToXLSX(exportRows, filename)}
              disabled={exportRows.length === 0}
              className="gap-1.5 text-xs h-8"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
            </Button>
          </div>
        </div>

        {/* Preview table */}
        {exportRows.length > 0 ? (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-0">
              <CardTitle className="text-sm text-gray-600 font-medium">
                Preview — first {previewRows.length} of {exportRows.length.toLocaleString()} rows
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 mt-2">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-400 font-medium uppercase tracking-wider bg-gray-50/70 border-y border-gray-100">
                      {previewCols.map((col) => (
                        <th key={col} className="px-4 py-2.5 whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {previewRows.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50/50">
                        {previewCols.map((col) => (
                          <td key={col} className="px-4 py-2 whitespace-nowrap text-gray-700 max-w-[200px] truncate">
                            {row[col] == null || row[col] === ''
                              ? <span className="text-gray-300">—</span>
                              : String(row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {exportRows.length > 10 && (
                <p className="px-4 py-2.5 text-xs text-gray-400 border-t border-gray-100">
                  +{(exportRows.length - 10).toLocaleString()} more rows in full export
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <FileSpreadsheet className="h-10 w-10 mb-3 opacity-20" />
            <p className="text-sm font-medium">No records match your filters</p>
            <p className="text-xs mt-1">Try clearing some filters</p>
          </div>
        )}
      </section>

    </div>
  );

  return (
    <>
      {jsx}
      {pdfClientObj && data && createPortal(
        <div
          ref={pdfRef}
          style={{
            position: 'fixed',
            top: 0,
            left: '-9999px',
            width: '794px',
            background: '#fff',
            zIndex: -1,
            pointerEvents: 'none',
          }}
        >
          <ComplianceReportDocument
            client={pdfClientObj}
            sites={data.sites.filter(s => s.client?.name === pdfClient)}
            contacts={data.contacts.filter(c => c.client_name === pdfClient)}
            contracts={pdfContractsByClient}
            dma={data.dma.filter(d => {
              const site = (d as unknown as { _site?: { name?: string } })._site;
              return site?.name === pdfClient;
            })}
            generatedAt={new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
